import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  IoDownloadOutline,
  IoShieldCheckmarkOutline,
  IoPauseOutline,
  IoCloseOutline,
  IoCheckmarkCircle,
  IoWarning,
} from 'react-icons/io5';
import { api, type ProductRow, type ProductsQuery } from '../api';
import { useAuth } from '../auth';

const PAGE_SIZE = 100;

type Filters = Omit<ProductsQuery, 'limit' | 'offset' | 'sort' | 'dir'>;
type SortKey = NonNullable<ProductsQuery['sort']>;
type SortDir = 'asc' | 'desc';

export function ProductsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const offset = (page - 1) * PAGE_SIZE;
  const filterValues = useQuery({ queryKey: ['products-filters'], queryFn: api.productFilters, staleTime: 60_000 });
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['products', filters, sort, sortDir, page],
    queryFn: () => api.products({ ...filters, sort, dir: sortDir, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const updateFilter = <K extends keyof Filters>(k: K, v: Filters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };
  const resetFilters = () => { setFilters({}); setPage(1); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const onSort = (key: SortKey) => {
    if (sort === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setSortDir('desc'); }
  };

  const bulkRrp = useMutation({
    mutationFn: (enabled: boolean) => {
      const filtersForBulk: Record<string, string> = {};
      for (const [k, v] of Object.entries(filters)) if (v) filtersForBulk[k] = String(v);
      return api.productsBulkRrpEnforce({ enabled, filters: filtersForBulk });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const items = data?.items ?? [];
  const firstShown = data && items.length > 0 ? offset + 1 : 0;
  const lastShown = data ? offset + items.length : 0;

  // Какие фильтры по атрибутам шин/дисков показывать (только если выбрана соответствующая категория)
  const showTireFilters = filters.category === 'Шины' || filters.category === 'Шина';
  const showDiskFilters = filters.category === 'Диск' || filters.category === 'Диски';

  return (
    <>
      <div className="page-header">
        <h2>Товары</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            className="btn"
            href={api.productsExportUrl({ ...filters, sort, dir: sortDir })}
            target="_blank"
            rel="noreferrer"
            title="Скачать CSV до 5000 строк, с учётом фильтров"
          >
            <IoDownloadOutline /> Экспорт CSV
          </a>
        </div>
      </div>

      <div className="filters">
        <div className="filter-field">
          <span className="filter-field-label">Поиск</span>
          <input className="input" placeholder="название / артикул"
            value={filters.q ?? ''}
            onChange={(e) => updateFilter('q', e.target.value || undefined)} />
        </div>
        <FilterSelect label="Категория" value={filters.category}
          options={filterValues.data?.categories.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` })) ?? []}
          onChange={(v) => updateFilter('category', v)} />
        <FilterSelect label="Бренд" value={filters.brand}
          options={filterValues.data?.brands.slice(0, 200).map((b) => ({ value: b.value, label: `${b.value} (${b.count})` })) ?? []}
          onChange={(v) => updateFilter('brand', v)} />

        {showTireFilters && (
          <>
            <FilterSelect label="Размер шины" value={filters.tireSize}
              options={filterValues.data?.tireSizes.map((t) => ({ value: t.value, label: `${t.value} (${t.count})` })) ?? []}
              onChange={(v) => updateFilter('tireSize', v)} />
            <FilterSelect label="Сезон" value={filters.tireSeason}
              options={filterValues.data?.tireSeasons.map((t) => ({ value: t.value, label: `${t.value} (${t.count})` })) ?? []}
              onChange={(v) => updateFilter('tireSeason', v)} />
          </>
        )}
        {showDiskFilters && (
          <>
            <FilterSelect label="Диаметр диска" value={filters.diskSize}
              options={filterValues.data?.diskSizes.map((d) => ({ value: d.value, label: `${d.value} (${d.count})` })) ?? []}
              onChange={(v) => updateFilter('diskSize', v)} />
            <FilterSelect label="PCD" value={filters.diskPcd}
              options={filterValues.data?.diskPcds.map((d) => ({ value: d.value, label: `${d.value} (${d.count})` })) ?? []}
              onChange={(v) => updateFilter('diskPcd', v)} />
          </>
        )}

        <FilterSelect label="Остатки" value={filters.stock} options={[
          { value: 'in', label: '✓ В наличии' },
          { value: 'out', label: '○ Нет в наличии' },
        ]} onChange={(v) => updateFilter('stock', v as Filters['stock'])} />
        <FilterSelect label="Индекс Ozon" value={filters.colorIndex} options={[
          { value: 'GREEN', label: '🟢 GREEN' },
          { value: 'YELLOW', label: '🟡 YELLOW' },
          { value: 'RED', label: '🔴 RED' },
          { value: 'WITHOUT_INDEX', label: '⚪ Без индекса' },
        ]} onChange={(v) => updateFilter('colorIndex', v as Filters['colorIndex'])} />
        <FilterSelect label="РРЦ" value={filters.rrp} options={[
          { value: 'set', label: '✓ Есть РРЦ' },
          { value: 'missing', label: '○ Без РРЦ' },
          { value: 'violation', label: '⚠ Нарушение' },
        ]} onChange={(v) => updateFilter('rrp', v as Filters['rrp'])} />
        <FilterSelect label="РРЦ-защита" value={filters.rrpEnforce} options={[
          { value: 'on', label: '🛡 Включена' },
          { value: 'off', label: '⏸ Выключена' },
        ]} onChange={(v) => updateFilter('rrpEnforce', v as Filters['rrpEnforce'])} />
        <FilterSelect label="Скидка Ozon-картой" value={filters.ozonDiscount} options={[
          { value: 'has', label: 'Цена снижена Ozon' },
          { value: 'none', label: 'Без скидки' },
        ]} onChange={(v) => updateFilter('ozonDiscount', v as Filters['ozonDiscount'])} />

        <div className="filter-field">
          <span className="filter-field-label">Цена, ₽</span>
          <div className="price-range">
            <input className="input" type="number" min={0} placeholder="от"
              value={filters.priceMin ?? ''}
              onChange={(e) => updateFilter('priceMin', e.target.value ? Number(e.target.value) : undefined)} />
            <span style={{ color: 'var(--text-muted)' }}>—</span>
            <input className="input" type="number" min={0} placeholder="до"
              value={filters.priceMax ?? ''}
              onChange={(e) => updateFilter('priceMax', e.target.value ? Number(e.target.value) : undefined)} />
          </div>
        </div>

        <div className="filter-actions">
          <span className="stats">
            {data && (
              <>Всего: <strong>{data.total}</strong>{activeFilterCount > 0 && ` (с фильтрами, активно: ${activeFilterCount})`}{isFetching && ' · обновление…'}</>
            )}
          </span>
          <div className="actions">
            {isAdmin && activeFilterCount > 0 && filters.rrp === 'set' && (
              <>
                <button className="btn" disabled={bulkRrp.isPending} onClick={() => {
                  if (confirm(`Включить РРЦ-защиту для ${data?.total ?? 0} товаров с текущими фильтрами?`)) bulkRrp.mutate(true);
                }} title="Включить РРЦ-защиту для всех под текущими фильтрами">
                  <IoShieldCheckmarkOutline /> Включить РРЦ
                </button>
                <button className="btn" disabled={bulkRrp.isPending} onClick={() => {
                  if (confirm(`Выключить РРЦ-защиту для ${data?.total ?? 0} товаров?`)) bulkRrp.mutate(false);
                }}>
                  <IoPauseOutline /> Выключить РРЦ
                </button>
              </>
            )}
            {activeFilterCount > 0 && <button className="btn" onClick={resetFilters}><IoCloseOutline /> Сбросить</button>}
          </div>
        </div>
      </div>

      {bulkRrp.data && (
        <div className="card" style={{ marginBottom: 12, background: 'var(--green-soft)', color: '#065f2e', display: 'flex', alignItems: 'center', gap: 8 }}>
          <IoCheckmarkCircle /> Обновлено товаров: {bulkRrp.data.updated}
        </div>
      )}

      {isLoading && <div className="card">Загрузка…</div>}
      {error && <div className="card" style={{ color: 'var(--red)' }}>{String((error as Error).message)}</div>}

      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span className="muted" style={{ fontSize: 13 }}>
              Показано {firstShown}–{lastShown} из {data.total}
            </span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>

          <table className="products">
            <thead>
              <tr>
                <th style={{ width: 56 }}></th>
                <SortHeader label="Название" k="name" sort={sort} dir={sortDir} onClick={onSort} />
                <SortHeader label="Артикул" k="offerId" sort={sort} dir={sortDir} onClick={onSort} />
                <SortHeader label="Бренд" k="brand" sort={sort} dir={sortDir} onClick={onSort} />
                <th>Характеристики</th>
                <SortHeader label="Наша цена" k="price" sort={sort} dir={sortDir} onClick={onSort} numeric />
                <th className="num" title="Цена которую видит клиент с Ozon Картой (marketing_seller_price)">Клиент с картой</th>
                <th>Индекс</th>
                <th className="num" title="Минимум на внешних площадках">Min внешн.</th>
                <th className="num" title="Минимум среди продавцов Ozon">Min Ozon</th>
                <SortHeader label="РРЦ" k="rrp" sort={sort} dir={sortDir} onClick={onSort} numeric />
                <th className="num">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => <ProductRowView key={p.productId} p={p} />)}
              {items.length === 0 && (
                <tr><td colSpan={12} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  {activeFilterCount > 0 ? 'Нет товаров под текущие фильтры.' : 'Каталог пуст.'}
                </td></tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </>
      )}
    </>
  );
}

function SortHeader({ label, k, sort, dir, onClick, numeric }: {
  label: string; k: SortKey; sort: SortKey; dir: SortDir; onClick: (k: SortKey) => void; numeric?: boolean;
}) {
  const active = sort === k;
  return (
    <th
      className={`sortable${active ? ' active' : ''}${numeric ? ' num' : ''}`}
      onClick={() => onClick(k)}
    >
      {label}
      <span className="sort-icon">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

interface OptionItem { value: string; label: string }

function FilterSelect({ label, value, options, onChange }: {
  label: string; value: string | undefined; options: OptionItem[]; onChange: (v: string | undefined) => void;
}) {
  return (
    <label className="filter-field">
      <span className="filter-field-label">{label}</span>
      <select
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">— все —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pager">
      <button className="btn" disabled={page <= 1} onClick={() => onChange(1)}>« 1</button>
      <button className="btn" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</button>
      <span className="page-info">стр. <strong>{page}</strong> из {totalPages}</span>
      <button className="btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</button>
      <button className="btn" disabled={page >= totalPages} onClick={() => onChange(totalPages)}>{totalPages} »</button>
    </div>
  );
}

function ProductRowView({ p }: { p: ProductRow }) {
  const brand = p.brandOfficial ?? p.brand;
  const attrs = useMemo(() => {
    const out: string[] = [];
    if (p.tireSize) out.push(p.tireSize);
    if (p.tireSeason) out.push(p.tireSeason);
    if (p.diskSize) out.push(p.diskSize);
    if (p.diskPcd) out.push(`PCD ${p.diskPcd}`);
    if (p.diskEt) out.push(`ET${p.diskEt}`);
    if (p.diskDia) out.push(`DIA ${p.diskDia}`);
    if (p.diskColor) out.push(p.diskColor);
    return out;
  }, [p]);
  const detailUrl = `/products/${p.productId}`;

  return (
    <tr>
      <td>
        <Link to={detailUrl}>
          {p.image
            ? <img src={p.image} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }} />
            : <div style={{ width: 44, height: 44, background: 'var(--bg)', borderRadius: 4 }} />}
        </Link>
      </td>
      <td>
        <Link to={detailUrl} style={{ color: 'inherit', textDecoration: 'none' }}>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{p.name}</div>
          {p.model && <div className="muted" style={{ fontSize: 11 }}>{p.model}</div>}
        </Link>
      </td>
      <td>
        <Link to={detailUrl} style={{ color: 'inherit', textDecoration: 'none' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.offerId}</div>
          {p.sku && <div className="muted" style={{ fontSize: 11 }}>{p.sku}</div>}
        </Link>
      </td>
      <td>
        <div style={{ fontSize: 13 }}>{brand ?? <span className="muted">—</span>}</div>
        {p.category && <div className="muted" style={{ fontSize: 11 }}>{p.category}</div>}
      </td>
      <td>
        {attrs.length === 0 ? <span className="muted">—</span> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: 200 }}>
            {attrs.map((a, i) => <span key={i} className="attr-pill">{a}</span>)}
          </div>
        )}
      </td>
      <td className="num">
        {p.price ? <strong>{Number(p.price).toFixed(0)} ₽</strong> : '—'}
        {p.oldPrice && Number(p.oldPrice) > 0 && (
          <div className="muted" style={{ fontSize: 11, textDecoration: 'line-through' }}>{Number(p.oldPrice).toFixed(0)} ₽</div>
        )}
      </td>
      <td className="num">
        {(() => {
          const ours = p.price ? Number(p.price) : null;
          const ozonCustomer = p.marketingSellerPrice ? Number(p.marketingSellerPrice) : null;
          if (!ozonCustomer || ozonCustomer <= 0) return <span className="muted">—</span>;
          const discounted = ours != null && ozonCustomer < ours;
          const diffPct = ours != null && ours > 0 ? ((ours - ozonCustomer) / ours) * 100 : 0;
          return (
            <>
              <strong style={{ color: discounted ? 'var(--red)' : undefined }}>{ozonCustomer.toFixed(0)} ₽</strong>
              {discounted && (
                <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 2 }} title="Ozon снизил цену для клиента (Ozon Card / акции)">
                  −{diffPct.toFixed(0)}% от нашей
                </div>
              )}
            </>
          );
        })()}
      </td>
      <td>
        {p.colorIndex ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span className={`color-dot ${p.colorIndex}`} />
            <span className="muted" style={{ fontSize: 11 }}>{p.colorIndex}</span>
          </span>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{fmtMin(p.extMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.ozonMinPrice, p.price)}</td>
      <td className="num">
        {p.rrpPrice ? (
          <>
            <div>{Number(p.rrpPrice).toFixed(0)} ₽</div>
            <div style={{ fontSize: 10, marginTop: 2 }}>
              {p.rrpEnforceEnabled ? (
                <span className="badge badge-success" style={{ fontSize: 10 }}>
                  <IoShieldCheckmarkOutline /> защита
                </span>
              ) : (
                <span className="badge" style={{ fontSize: 10 }}>
                  <IoPauseOutline /> откл
                </span>
              )}
            </div>
            {p.rrpViolation && (
              <div style={{ fontSize: 10, color: 'var(--red)', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}
                   title={`Эффективная: ${p.rrpViolation.effective}₽, ниже на ${(p.rrpViolation.gapPct * 100).toFixed(1)}%`}>
                <IoWarning /> {(p.rrpViolation.gapPct * 100).toFixed(0)}% ниже
              </div>
            )}
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{p.stockTotal > 0 ? <strong style={{ color: 'var(--green)' }}>{p.stockTotal}</strong> : <span className="muted">0</span>}</td>
    </tr>
  );
}

function fmtMin(competitor: string | null, ours: string | null): React.ReactNode {
  if (!competitor || competitor === '0' || competitor === '0.00') return <span className="muted">—</span>;
  const c = Number(competitor);
  const o = ours ? Number(ours) : null;
  if (!Number.isFinite(c) || c <= 0) return <span className="muted">—</span>;
  const color = o == null ? undefined : c < o ? 'var(--red)' : c > o ? 'var(--green)' : undefined;
  return <span style={{ color }}>{c.toFixed(0)} ₽</span>;
}
